package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextDirection
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import coil3.compose.AsyncImage
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Immersive full-screen photo viewer (tap a cover/gallery photo → see it big, swipe between photos).
 *
 * Copy-free per the design-system rules: the close a11y label, the per-image a11y label and the
 * "n / total" counter format all come from the caller as localized strings. Rendered as a
 * full-bleed [Dialog] (`usePlatformDefaultWidth = false`) over [AppTheme.colors.scrim]; back press
 * and tapping anywhere off the controls dismiss it.
 *
 * Pinch-to-zoom is intentionally out of scope for now (needs shared transform-gesture plumbing);
 * add it here — never per-screen — when it lands.
 *
 * @param imageUrls the photos to page through (swipe / [HorizontalPager]); empty list renders nothing.
 * @param closeContentDescription localized a11y label for the close button.
 * @param startIndex which photo to open on (coerced into range).
 * @param counterFormat localized position format containing `%1$d` (current, 1-based) and `%2$d`
 *   (total), e.g. `"%1$d / %2$d"`. Null (or a single photo) hides the counter.
 * @param imageContentDescription localized a11y label applied to each photo; null = decorative.
 */
@Composable
fun FullScreenImageViewer(
    imageUrls: List<String>,
    closeContentDescription: String,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    startIndex: Int = 0,
    counterFormat: String? = null,
    imageContentDescription: String? = null,
) {
    if (imageUrls.isEmpty()) return
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false),
    ) {
        val pagerState = rememberPagerState(
            initialPage = startIndex.coerceIn(0, imageUrls.lastIndex),
            pageCount = { imageUrls.size },
        )
        Box(
            modifier = modifier
                .fillMaxSize()
                .background(AppTheme.colors.scrim)
                // The whole backdrop dismisses (taps fall through the pager's images to here).
                // No indication: this is a scrim, not a button.
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onDismiss,
                ),
        ) {
            HorizontalPager(state = pagerState, modifier = Modifier.fillMaxSize()) { page ->
                AsyncImage(
                    model = imageUrls[page],
                    contentDescription = imageContentDescription,
                    contentScale = ContentScale.Fit,
                    modifier = Modifier.fillMaxSize(),
                )
            }
            AppIconButton(
                iconName = "close",
                contentDescription = closeContentDescription,
                onClick = onDismiss,
                tint = AppTheme.colors.onScrim,
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .safeContentPadding()
                    .padding(AppTheme.spacing.md),
            )
            if (counterFormat != null && imageUrls.size > 1) {
                Text(
                    text = counterFormat.formatPosition(pagerState.currentPage + 1, imageUrls.size),
                    // Force LTR so "1 / 2" reads in order even under an RTL (Farsi) layout — otherwise the
                    // bidi algorithm reorders the neutral "/" and the counter renders reversed as "2 / 1".
                    style = AppTheme.typography.labelMd.copy(textDirection = TextDirection.Ltr),
                    color = AppTheme.colors.onScrim,
                    modifier = Modifier
                        .align(Alignment.BottomCenter)
                        .safeContentPadding()
                        .padding(AppTheme.spacing.lg),
                )
            }
        }
    }
}

/**
 * Fills `%1$d`/`%2$d` in a caller-localized position format. Done by hand because commonMain has no
 * `String.format`, and compose-resources formatting is tied to `stringResource` at the call site.
 */
private fun String.formatPosition(current: Int, total: Int): String =
    replace("%1\$d", current.toString()).replace("%2\$d", total.toString())
